import {useCallback} from "react";
import {useReown} from "@/context/AuthContext.tsx";


type WithToken<T> = T extends void | undefined
  ? { token?: string }
  : T & { token?: string };


export function useAuthQuery<TArg, TResult>(
  useQuery: (arg: any, options?: any) => TResult,
  arg?: TArg,
  options?: any
): TResult {
  const {token} = useReown();
  // console.log('za.helper',token)

  const finalArgs = (arg && typeof arg === 'object'
    ? {...arg, token}
    : {token}) as WithToken<TArg>;

  return useQuery(finalArgs, options);
}

export function useAuthMutation<
  TResult extends readonly [any, any]
>(
  useMutation: () => TResult
): TResult {
  const { token } = useReown();
  const [trigger, result] = useMutation();
  const wrappedTrigger = useCallback(
    (arg: any) => trigger({ ...arg, token }),
    [token, trigger],
  );

  return [wrappedTrigger, result] as unknown as TResult;
}

export function useAuthFileMutation<TResult extends readonly [any, any]>(
  useMutation: () => TResult
): TResult {
  const { token } = useReown();
  const [trigger, result] = useMutation();
  const wrappedTrigger = useCallback(
    (file: File) => trigger({ file, token } as any),
    [token, trigger]
  );
  return [wrappedTrigger, result] as unknown as TResult;
}

export function useAuthLazyQuery<
  TArg,
  TResult extends readonly [any, any, any]
>(
  useLazyQuery: () => TResult
): TResult {
  const { token } = useReown();
  const [trigger, result, lastPromiseInfo] = useLazyQuery();

  // Добавляем "?" к arg, чтобы разрешить вызов trigger() без параметров.
  // useCallback обязателен: иначе новая ссылка на каждом рендере ломает useCallback/useEffect в потребителях.
  const wrappedTrigger = useCallback(
    (arg?: TArg, preferCacheValue?: boolean) => {
      const finalArgs = (arg && typeof arg === "object"
        ? { ...arg, token }
        : { token }) as any;

      return trigger(finalArgs, preferCacheValue);
    },
    [token, trigger],
  );

  return [wrappedTrigger, result, lastPromiseInfo] as unknown as TResult;
}